import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import { useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { logError } from '../../../../utils/logger';
import { ConfirmationModal, SectionTitle } from '../../../shared/components';
import { formatAmount } from '../../../shared/utils/misc';
import { HIRING_FORM_MODE } from '../hiringRequest.constant';
import { deleteHiringRequest } from '../hiringRequest.service';
import HiringRequestForm from './HiringRequestForm';

export default function JobPostingDetails() {
  const { jobPostings, onRefresh } = useOutletContext<Record<string, any>>();
  const { jobPostingId } = useParams();
  const navigate = useNavigate();

  const [editing, setEditing] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteHiringRequest(jobPostingId);
      toast.success('Job posting deleted successfully');
      setOpenDeleteConfirm(false);
      onRefresh?.();
      navigate('/hiring-request');
    } catch (err) {
      toast.error(
        logError({
          message: 'Failed to delete job posting',
          error: err,
          code: 'JOB_POST_DELETE_FAILED',
        })
      );
    } finally {
      setDeleting(false);
    }
  };

  const job = jobPostings.find((j) => String(j.id) === String(jobPostingId));

  return job ? (
    <>
      <div className='column gap-3'>
        <SectionTitle
          title={`Hiring for ${job.designation}`}
          action={
            <div className='row gap-3'>
              <button
                type='button'
                className='btn text-btn text-btn--small'
                onClick={() => setEditing(true)}
                disabled={editing || deleting}
              >
                <Icon icon='line-md:edit-twotone' width={16} />
                Edit
              </button>
              <button
                type='button'
                className='btn text-btn text-btn--small'
                onClick={() => setOpenDeleteConfirm(true)}
                disabled={editing || deleting}
              >
                <Icon icon='material-symbols:delete-outline' width={16} />
                Delete
              </button>
            </div>
          }
        />
      </div>
      {editing ? (
        <HiringRequestForm
          mode={HIRING_FORM_MODE.EDIT}
          posting={{ ...job, responsibilities: job.responsibilities.join('\n') }}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <>
          <div className='grid grid--2-cols'>
            <div className='data-grid-column'>
              <div className='data-grid-row'>
                <span className='font-semibold'>Department</span>
                <span>{job.department}</span>
              </div>
              <div className='data-grid-row'>
                <span className='font-semibold'>Employment type</span>
                <span>{job.employmentType}</span>
              </div>
              <div className='data-grid-row'>
                <span className='font-semibold'>Gender preferred</span>
                <span>{job.genderPreference} preferred</span>
              </div>
              <div className='data-grid-row'>
                <span className='font-semibold'>Qualification preferred</span>
                <span>{job.qualificationPreference} preferred</span>
              </div>
            </div>
            <div className='data-grid-column'>
              <div className='data-grid-row'>
                <span className='font-semibold'>Designation</span>
                <span>{job.designation}</span>
              </div>
              <div className='data-grid-row'>
                <span className='font-semibold'>Work arrangement</span>
                <span>{job.workArrangement}</span>
              </div>
              <div className='data-grid-row'>
                <span className='font-semibold'>Age group</span>
                <span>
                  {job.minAge} to {job.maxAge}
                </span>
              </div>
              <div className='data-grid-row'>
                <span className='font-semibold'>Experience required</span>
                <span>
                  {job.minExperience} - {job.maxExperience}
                </span>
              </div>
            </div>
          </div>
          <div className='column'>
            <span className='font-semibold'>Roles and Responsibilities</span>
            <ul className='column'>
              {job.responsibilities.map((role, index) => (
                <li key={index} className='list-disc'>
                  <span className='font-md'>{role}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className='column'>
            <span className='font-semibold'>Tools required</span>
            <ul className='column'>
              {job.toolsRequired.map((tool, index) => (
                <li key={index} className='list-disc'>
                  <span className='font-md'>{tool}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className='data-grid-column'>
            <div className='data-grid-row'>
              <span className='font-semibold'>To offer CTC</span>
              <span>
                {formatAmount(job.minCTC)} - {formatAmount(job.maxCTC)}
              </span>
            </div>
            <div className='data-grid-row'>
              <span className='font-semibold'>No. of positions</span>
              <span>{job.noOfPositions}</span>
            </div>
          </div>
        </>
      )}
      <ConfirmationModal
        open={openDeleteConfirm}
        onClose={() => setOpenDeleteConfirm(false)}
        title='Delete job posting'
        message='Are you sure you want to delete this job posting?'
        onConfirm={handleDelete}
        icon='material-symbols:delete-outline'
        loading={deleting}
      />
    </>
  ) : (
    <Alert severity='error'>Job not found</Alert>
  );
}
